import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  CodeBracketIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Template, TemplateUsage } from '../../types/template';
import { TemplateTypeBadge } from '../../components/Templates/TemplateTypeBadge';
import { TemplateStatusBadge } from '../../components/Templates/TemplateStatusBadge';
import { TemplateLoadingSkeleton } from '../../components/Templates/TemplateLoadingSkeleton';

export const TemplateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<Record<string, any>>({});

  // Fetch template
  const { data: template, isLoading, error } = useQuery<Template>({
    queryKey: ['template', id],
    queryFn: async () => {
      const response = await api.templates.get(id!);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch template usage (mock for now - would need backend endpoint)
  const { data: usage } = useQuery<TemplateUsage[]>({
    queryKey: ['template-usage', id],
    queryFn: async () => {
      // This would be a real API call in production
      // For now, return empty array
      return [];
    },
    enabled: !!id,
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: async (variables: Record<string, any>) => {
      const response = await api.templates.preview(id!, variables);
      return response.data;
    },
    onSuccess: () => {
      setShowPreview(true);
    },
    onError: (error: any) => {
      toast.error(`Failed to generate preview: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.templates.duplicate(id!);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template duplicated successfully');
      navigate(`/templates/${data.id}/edit`);
    },
    onError: (error: any) => {
      toast.error(`Failed to duplicate template: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.templates.delete(id!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template deleted successfully');
      navigate('/templates');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete template: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleEdit = () => {
    navigate(`/templates/${id}/edit`);
  };

  const handleDuplicate = () => {
    duplicateMutation.mutate();
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const handlePreview = () => {
    // Use existing preview data or create sample data
    const sampleData = template?.variables?.reduce((acc, variable) => {
      acc[variable] = `Sample ${variable}`;
      return acc;
    }, {} as Record<string, any>) || {};
    
    setPreviewData(sampleData);
    previewMutation.mutate(sampleData);
  };


  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <TemplateLoadingSkeleton type="detail" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !template) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Template not found</h3>
              <p className="mt-1 text-sm text-gray-500">The template you're looking for doesn't exist.</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/templates')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  Back to Templates
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/templates')}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Templates
        </button>

        {/* Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">{template.name}</h1>
                  <TemplateTypeBadge type={template.template_type} />
                  <TemplateStatusBadge status={template.status} />
                </div>
                {template.description && (
                  <p className="text-sm text-gray-500">{template.description}</p>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={handlePreview}
                  disabled={previewMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Preview
                </button>
                <button
                  onClick={handleDuplicate}
                  disabled={duplicateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                  Duplicate
                </button>
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
                {(template.status === 'draft' || template.status === 'archived') && (
                  <button
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="px-6 py-4 bg-gray-50 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center text-sm">
              <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-gray-500">Created</p>
                <p className="font-medium text-gray-900">
                  {new Date(template.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-gray-500">Updated</p>
                <p className="font-medium text-gray-900">
                  {new Date(template.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <CodeBracketIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-gray-500">Variables</p>
                <p className="font-medium text-gray-900">
                  {template.variables?.length || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-gray-500">Usage Count</p>
                <p className="font-medium text-gray-900">
                  {template.usage_count || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email Subject (for email templates) */}
            {template.template_type === 'email' && template.subject && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Email Subject</h2>
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-sm text-gray-900 font-mono">{template.subject}</p>
                </div>
              </div>
            )}

            {/* Template Content */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Template Content</h2>
              <div className="bg-gray-50 rounded-md p-4">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono overflow-x-auto">
                  {template.content}
                </pre>
              </div>
              {template.template_type === 'sms' && (
                <p className="mt-2 text-sm text-gray-500">
                  Character count: {template.content.length} / 160
                </p>
              )}
            </div>

            {/* Preview (if generated) */}
            {showPreview && previewMutation.data && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Preview</h2>
                {previewMutation.data.rendered_subject && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Subject:</h3>
                    <div className="bg-gray-50 rounded-md p-3">
                      <p className="text-sm text-gray-900">{previewMutation.data.rendered_subject}</p>
                    </div>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Content:</h3>
                  <div 
                    className="bg-gray-50 rounded-md p-3 prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: previewMutation.data.rendered_content }}
                  />
                </div>
              </div>
            )}

            {/* Usage History */}
            {usage && usage.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Usage History</h2>
                <div className="space-y-3">
                  {usage.map((item) => (
                    <div key={item.campaign_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.campaign_name}</p>
                        <p className="text-xs text-gray-500">
                          Last used: {new Date(item.last_used).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.sent_count} sent
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Variables */}
            {template.variables && template.variables.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Variables</h2>
                <div className="space-y-2">
                  {template.variables.map((variable) => (
                    <div
                      key={variable}
                      className="flex items-center justify-between p-2 bg-blue-50 rounded-md"
                    >
                      <code className="text-sm text-blue-900 font-mono">
                        {`{{${variable}}}`}
                      </code>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-gray-500">
                  These variables will be replaced with actual values when the template is used.
                </p>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/campaigns/new?template=${id}`)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Use in Campaign
                </button>
                <button
                  onClick={handleEdit}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Edit Template
                </button>
                <button
                  onClick={handleDuplicate}
                  disabled={duplicateMutation.isPending}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">Delete Template</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete "{template.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

